/**
 * useConnectorStats - Hook for fetching global connector statistics
 *
 * Aggregates execution statistics across all connectors for dashboard display.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type { ConnectorType } from '@/types/connectors';

const API_BASE = '/connectors';

/**
 * Global connector statistics aggregated across all connectors
 */
export interface ConnectorStats {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  successRate: number;
  totalRecordsProcessed: number;
  totalBytesWritten: number;
  avgDurationSeconds: number;
  activeConnectors: number;
  disabledConnectors: number;
  runningExecutions: number;
}

/**
 * Per-connector statistics for health grid
 */
export interface ConnectorHealthInfo {
  /** Unique per configuration; several configs can share a connector_type. */
  connector_config_id?: string | null;
  connector_type: ConnectorType;
  name: string;
  enabled: boolean;
  last_execution_at: string | null;
  last_execution_status: string | null;
  recent_success_rate: number | null;
  executions_24h: number;
  executions_period: number;
  records_processed: number;
  avg_duration_seconds: number | null;
  health_status: 'healthy' | 'warning' | 'error' | 'disabled' | 'unknown';
}

/**
 * Time series data for execution trends
 */
export interface ExecutionTrendData {
  date: string;
  successful: number;
  failed: number;
  total: number;
}

/**
 * Recent error information
 */
export interface RecentError {
  execution_id: string;
  connector_config_id?: string | null;
  connector_type: ConnectorType;
  connector_name: string;
  error_message: string;
  timestamp: string;
  query_name?: string;
}

/**
 * Complete statistics response with all dashboard data
 */
export interface ConnectorStatsResponse {
  summary: ConnectorStats;
  previous_summary?: ConnectorStats;
  connector_health: ConnectorHealthInfo[];
  execution_trends: ExecutionTrendData[];
  recent_errors: RecentError[];
  period_days: number;
  /** Resolved primary/comparison windows echoed by the backend (ISO datetimes). */
  period_start?: string;
  period_end?: string;
  compare_start?: string;
  compare_end?: string;
  generated_at: string;
}

/**
 * Statistics query: either a preset period (periodDays) or an explicit primary
 * window (start/end), optionally with a custom comparison window.
 */
export interface StatsQuery {
  periodDays?: number;
  start?: string; // ISO datetime
  end?: string; // ISO datetime (exclusive upper bound)
  compareStart?: string;
  compareEnd?: string;
}

function toQuery(query?: number | StatsQuery): StatsQuery {
  if (query === undefined) return { periodDays: 7 };
  return typeof query === 'number' ? { periodDays: query } : query;
}

/**
 * Fetch global connector statistics aggregated across all connectors
 *
 * This endpoint should aggregate data from all connectors to provide
 * dashboard-level metrics. If the backend doesn't provide a global
 * stats endpoint, we'll aggregate from individual connectors.
 */
async function fetchConnectorStats(query: StatsQuery): Promise<ConnectorStatsResponse> {
  // Explicit primary window takes precedence over period_days; the comparison
  // window is sent only when both bounds are present (else the backend derives it).
  const params: Record<string, string | number> =
    query.start && query.end
      ? {
          start: query.start,
          end: query.end,
          ...(query.compareStart && query.compareEnd
            ? { compare_start: query.compareStart, compare_end: query.compareEnd }
            : {}),
        }
      : { period_days: query.periodDays ?? 7 };

  try {
    // Fetch from the global stats endpoint
    // Note: The endpoint is /connectors/statistics (not just /statistics)
    // This is because the apiClient baseURL is /api/v1, and we need to hit
    // /api/v1/connectors/statistics
    const response = await apiClient.get(`${API_BASE}/statistics`, { params });
    return response.data;
  } catch (error: any) {
    // If endpoint fails (404 or 422), fallback to aggregating from connector list.
    // The fallback is periodDays-only (no per-connector arbitrary-window source).
    if (error?.response?.status === 404 || error?.response?.status === 422) {
      return await aggregateStatsFromConnectors(query.periodDays ?? 7);
    }
    throw error;
  }
}

/**
 * Fallback: Aggregate statistics from individual connector endpoints
 */
async function aggregateStatsFromConnectors(periodDays: number): Promise<ConnectorStatsResponse> {
  // Fetch all connectors
  const connectorsResponse = await apiClient.get(`${API_BASE}/`);
  const connectors = connectorsResponse.data.connectors || [];

  // Initialize aggregate stats
  let totalExecutions = 0;
  let successfulExecutions = 0;
  let failedExecutions = 0;
  let totalRecordsProcessed = 0;
  let totalBytesWritten = 0;
  let totalDuration = 0;
  let durationCount = 0;
  let activeConnectors = 0;
  let disabledConnectors = 0;
  let runningExecutions = 0;

  const connectorHealth: ConnectorHealthInfo[] = [];
  const recentErrors: RecentError[] = [];
  const trendMap: Record<string, { successful: number; failed: number }> = {};

  // Fetch stats for each connector
  for (const connector of connectors) {
    const connectorType = connector.connector_type;

    if (!connector.enabled) {
      disabledConnectors++;
    } else {
      activeConnectors++;
    }

    try {
      // Fetch connector-specific stats
      const statsResponse = await apiClient.get(
        `${API_BASE}/${connectorType}/stats`,
        { params: { period_days: periodDays } }
      );
      const stats = statsResponse.data;

      // Aggregate totals
      totalExecutions += stats.total_executions || 0;
      successfulExecutions += stats.successful_executions || 0;
      failedExecutions += stats.failed_executions || 0;
      totalRecordsProcessed += stats.total_records_processed || 0;
      totalBytesWritten += stats.total_bytes_written || 0;

      if (stats.avg_duration_seconds) {
        totalDuration += stats.avg_duration_seconds * (stats.total_executions || 1);
        durationCount += stats.total_executions || 1;
      }

      // Count running executions from latest execution
      if (connector.last_execution_status === 'running') {
        runningExecutions++;
      }

      // Build connector health info
      const successRate = stats.total_executions > 0
        ? (stats.successful_executions / stats.total_executions) * 100
        : null;

      let healthStatus: ConnectorHealthInfo['health_status'] = 'unknown';
      if (!connector.enabled) {
        healthStatus = 'disabled';
      } else if (connector.last_execution_status === 'failed') {
        healthStatus = 'error';
      } else if (successRate !== null && successRate < 80) {
        healthStatus = 'warning';
      } else if (connector.last_execution_at) {
        healthStatus = 'healthy';
      }

      connectorHealth.push({
        connector_type: connectorType,
        name: connector.name || connectorType,
        enabled: connector.enabled || false,
        last_execution_at: connector.last_execution_at || null,
        last_execution_status: connector.last_execution_status || null,
        recent_success_rate: successRate,
        executions_24h: stats.total_executions || 0,
        executions_period: stats.total_executions || 0,
        records_processed: stats.total_records_processed || 0,
        avg_duration_seconds: stats.avg_duration_seconds ?? null,
        health_status: healthStatus,
      });

      // Try to fetch recent errors (if metrics endpoint provides them)
      try {
        const metricsResponse = await apiClient.get(
          `${API_BASE}/${connectorType}/metrics`,
          { params: { period_days: periodDays } }
        );

        if (metricsResponse.data.recent_errors) {
          metricsResponse.data.recent_errors.forEach((error: any) => {
            recentErrors.push({
              execution_id: error.execution_id,
              connector_type: connectorType,
              connector_name: connector.name || connectorType,
              error_message: error.error_message,
              timestamp: error.timestamp,
              query_name: error.query_name,
            });
          });
        }

        // Build execution trends from time series data
        if (metricsResponse.data.executions_over_time) {
          metricsResponse.data.executions_over_time.forEach((point: any) => {
            if (!trendMap[point.date]) {
              trendMap[point.date] = { successful: 0, failed: 0 };
            }
            trendMap[point.date].successful += point.successful || 0;
            trendMap[point.date].failed += point.failed || 0;
          });
        }
      } catch {
        // Metrics endpoint might not exist for all connectors
      }
    } catch (error) {
      // Skip connectors that don't have stats endpoints
      console.warn(`Failed to fetch stats for ${connectorType}:`, error);
    }
  }

  // Calculate success rate
  const successRate = totalExecutions > 0
    ? (successfulExecutions / totalExecutions) * 100
    : 0;

  // Calculate average duration
  const avgDurationSeconds = durationCount > 0
    ? totalDuration / durationCount
    : 0;

  // Convert trend map to array and sort by date
  const executionTrends: ExecutionTrendData[] = Object.entries(trendMap)
    .map(([date, data]) => ({
      date,
      successful: data.successful,
      failed: data.failed,
      total: data.successful + data.failed,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // Sort recent errors by timestamp (most recent first)
  recentErrors.sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  return {
    summary: {
      totalExecutions,
      successfulExecutions,
      failedExecutions,
      successRate,
      totalRecordsProcessed,
      totalBytesWritten,
      avgDurationSeconds,
      activeConnectors,
      disabledConnectors,
      runningExecutions,
    },
    connector_health: connectorHealth,
    execution_trends: executionTrends.slice(-periodDays), // Last N days
    recent_errors: recentErrors.slice(0, 10), // Top 10 most recent
    period_days: periodDays,
    generated_at: new Date().toISOString(),
  };
}

/**
 * Hook to fetch global connector statistics
 *
 * Provides aggregated metrics across all connectors for the statistics dashboard.
 *
 * @param query - A preset period in days (number, default 7) or a StatsQuery with an
 *   explicit primary window (start/end) and optional comparison window
 *   (compareStart/compareEnd).
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data: stats } = useConnectorStats(7);
 * const { data } = useConnectorStats({ start, end, compareStart, compareEnd });
 * // Access: stats.summary, stats.connector_health, stats.execution_trends
 */
export function useConnectorStats(
  query?: number | StatsQuery,
  enabled: boolean = true
) {
  const q = toQuery(query);
  return useQuery({
    queryKey: [
      'connectorStats',
      q.periodDays ?? null,
      q.start ?? null,
      q.end ?? null,
      q.compareStart ?? null,
      q.compareEnd ?? null,
    ],
    queryFn: () => fetchConnectorStats(q),
    enabled,
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // Auto-refresh every 5 minutes
  });
}
